import { Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { ChevronDown, Check } from "lucide-react";
import { useLang, type Lang } from "@/lib/i18n";
import { useEntry, useEntries, useField } from "@/lib/content";
import tacoyaLogoAsset from "@/assets/tacoya-logo.png.asset.json";
const tacoyaLogo = tacoyaLogoAsset.url;

// Tiny inline flag SVGs — render reliably on every OS (no emoji fallback).
function FlagGB({ className = "" }: { className?: string }) {
  return (
    <svg viewBox="0 0 60 30" className={className} aria-hidden>
      <clipPath id="t"><path d="M0,0 v30 h60 v-30 z" /></clipPath>
      <clipPath id="s"><path d="M30,15 h30 v15 z v15 h-30 z h-30 v-15 z v-15 h30 z" /></clipPath>
      <g clipPath="url(#t)">
        <path d="M0,0 v30 h60 v-30 z" fill="#012169" />
        <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" strokeWidth="6" />
        <path d="M0,0 L60,30 M60,0 L0,30" clipPath="url(#s)" stroke="#C8102E" strokeWidth="4" />
        <path d="M30,0 v30 M0,15 h60" stroke="#fff" strokeWidth="10" />
        <path d="M30,0 v30 M0,15 h60" stroke="#C8102E" strokeWidth="6" />
      </g>
    </svg>
  );
}

function FlagFI({ className = "" }: { className?: string }) {
  return (
    <svg viewBox="0 0 18 11" className={className} aria-hidden>
      <rect width="18" height="11" fill="#fff" />
      <rect x="5" width="3" height="11" fill="#003580" />
      <rect y="4" width="18" height="3" fill="#003580" />
    </svg>
  );
}

const LANGUAGES = [
  { code: "EN" as const, label: "English", Flag: FlagGB },
  { code: "FI" as const, label: "Suomi", Flag: FlagFI },
] as const;

type LangCode = Lang;

function LangSwitch({ className = "" }: { className?: string }) {
  const { lang, setLang } = useLang();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const choose = (l: LangCode) => {
    setOpen(false);
    setLang(l);
  };

  const current = LANGUAGES.find((l) => l.code === lang) ?? LANGUAGES[0];
  const Current = current.Flag;

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Language: ${current.label}`}
        className="group relative inline-flex items-center justify-center size-10 rounded-full border border-cream/15 bg-cream/[0.04] backdrop-blur-md text-cream hover:border-yellow/60 hover:bg-cream/[0.08] transition-all shadow-[0_4px_20px_-8px_rgba(0,0,0,0.6)]"
      >
        <span className="relative flex items-center justify-center size-7 overflow-hidden rounded-full ring-1 ring-cream/30 shrink-0 aspect-square">
          <Current className="absolute inset-0 h-full w-full object-cover scale-[1.8]" />
        </span>
        <ChevronDown className={`absolute -bottom-0.5 -right-0.5 size-3.5 p-0.5 rounded-full bg-charcoal border border-cream/20 opacity-80 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute right-0 top-full mt-2 min-w-[180px] rounded-xl border border-cream/10 bg-charcoal/95 backdrop-blur-xl shadow-[0_20px_50px_-20px_rgba(0,0,0,0.9)] p-1 z-50 origin-top-right animate-scale-in"
        >
          {LANGUAGES.map((l) => {
            const active = l.code === lang;
            const F = l.Flag;
            return (
              <button
                key={l.code}
                type="button"
                role="option"
                aria-selected={active}
                onClick={() => choose(l.code)}
                className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-left text-xs transition-colors ${
                  active ? "bg-yellow/10 text-yellow" : "text-cream/80 hover:bg-cream/[0.06] hover:text-cream"
                }`}
              >
                <span className="relative flex items-center justify-center size-6 overflow-hidden rounded-full ring-1 ring-cream/30 shrink-0 aspect-square">
                  <F className="absolute inset-0 h-full w-full object-cover scale-[1.8]" />
                </span>
                <span className="flex-1">{l.label}</span>
                <span className="font-mono text-[10px] tracking-widest text-cream/70">{l.code}</span>
                {active && <Check className="size-3.5 text-yellow" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function Header() {
  const f = useField();
  const brand = useEntry("header", "brand");
  const navEntries = useEntries("header", "nav");
  const brandName = f(brand, "name", "TACOYA");
  const links = navEntries.map((n) => ({ to: String(n.data.to ?? "/"), label: f(n, "label") }));
  return (
    <header className="absolute top-2 md:top-3 inset-x-2 md:inset-x-3 z-50">
      {/* Mobile bar (unchanged behavior) */}
      <div className="md:hidden mx-auto max-w-7xl">
        <div className="relative flex items-center justify-between gap-3 h-12 px-3 rounded-2xl border border-cream/15 bg-gradient-to-b from-charcoal/95 via-charcoal/85 to-charcoal/95 backdrop-blur-xl shadow-[0_18px_40px_-15px_rgba(0,0,0,0.85),inset_0_1px_0_0_rgba(255,255,255,0.12),inset_0_-1px_0_0_rgba(0,0,0,0.4)]">
          {/* glossy top highlight */}
          <span className="pointer-events-none absolute inset-x-0 top-0 h-1/2 rounded-t-2xl bg-gradient-to-b from-cream/[0.10] to-transparent" />
          <span className="pointer-events-none absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-yellow/40 to-transparent" />
          <Link to="/franchise" className="relative flex items-center gap-2 min-w-0">
            <img src={tacoyaLogo} alt="Tacoya Birrieria" className="h-8 w-auto object-contain shrink-0" />
            <span className="font-display text-lg tracking-[0.22em] text-cream truncate">{brandName}</span>
          </Link>
          <div className="relative flex items-center gap-2">
            <LangSwitch />
          </div>
        </div>
      </div>

      {/* Desktop bar — advanced floating glass layout */}
      <div className="hidden md:block mx-auto max-w-[1280px]">
        <div className="group/nav relative grid grid-cols-[auto_1fr_auto] items-center gap-2 lg:gap-6 h-[60px] lg:h-[68px] pl-2 pr-1.5 rounded-full border border-cream/15 bg-gradient-to-b from-[#2a2520]/95 via-charcoal/85 to-[#1a1612]/95 backdrop-blur-2xl shadow-[0_25px_70px_-20px_rgba(0,0,0,0.95),inset_0_1px_0_0_rgba(255,255,255,0.18),inset_0_-1px_0_0_rgba(0,0,0,0.5),inset_0_0_0_1px_rgba(255,255,255,0.03)]">
          {/* glossy top sheen — like a wet pill */}
          <span className="pointer-events-none absolute inset-x-0 top-0 h-1/2 rounded-t-full bg-gradient-to-b from-cream/[0.14] via-cream/[0.04] to-transparent" />
          {/* bottom inner shadow for depth */}
          <span className="pointer-events-none absolute inset-x-0 bottom-0 h-1/3 rounded-b-full bg-gradient-to-t from-black/40 to-transparent" />
          {/* top gradient hairline (specular) */}
          <span className="pointer-events-none absolute inset-x-12 top-0 h-px bg-gradient-to-r from-transparent via-cream/60 to-transparent" />
          {/* subtle ambient warm glow */}
          <span className="pointer-events-none absolute -inset-px rounded-full bg-[radial-gradient(70%_130%_at_50%_0%,color-mix(in_oklab,var(--yellow)_14%,transparent),transparent_60%)]" />
          {/* moving shine sweep */}
          <span aria-hidden className="pointer-events-none absolute inset-0 rounded-full overflow-hidden">
            <span className="absolute top-0 -left-1/3 h-full w-1/3 bg-gradient-to-r from-transparent via-cream/15 to-transparent skew-x-[-20deg] translate-x-[-100%] group-hover/nav:translate-x-[400%] transition-transform duration-[1400ms] ease-out" />
          </span>

          {/* LEFT — Logo cluster */}
          <Link to="/franchise" className="relative flex items-center gap-2 lg:gap-3 group pl-1.5 min-w-0">
            <span className="relative flex items-center justify-center size-10 lg:size-12 rounded-full bg-cream/[0.04] ring-1 ring-cream/10 group-hover:ring-yellow/40 transition shrink-0">
              <img
                src={tacoyaLogo}
                alt="Tacoya Birrieria"
                className="h-9 w-9 lg:h-11 lg:w-11 object-contain transition-transform group-hover:scale-110 group-hover:rotate-[-3deg]"
              />
            </span>
            <div className="flex flex-col leading-none min-w-0">
              <span className="font-display text-xl sm:text-2xl lg:text-3xl tracking-[0.22em] text-cream truncate">{brandName}</span>
            </div>
          </Link>

          {/* CENTER — Nav pill */}
          <nav className="justify-self-center flex items-center gap-0.5 p-1 rounded-full bg-charcoal/60 ring-1 ring-cream/[0.06] shadow-inner">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to as any}
                preload="intent"
                className="group relative px-2.5 lg:px-4 py-1.5 lg:py-2 rounded-full text-[10px] lg:text-[11px] uppercase tracking-[0.18em] lg:tracking-[0.22em] font-semibold text-cream/70 hover:text-cream transition-colors whitespace-nowrap data-[status=active]:text-charcoal"
                activeProps={{
                  className:
                    "text-charcoal bg-yellow shadow-[0_6px_18px_-6px_color-mix(in_oklab,var(--yellow)_55%,transparent)]",
                }}
                activeOptions={{ exact: l.to === "/" }}
              >
                <span className="relative z-10">{l.label}</span>
                <span
                  aria-hidden
                  className="pointer-events-none absolute inset-1 rounded-full bg-cream/[0.06] opacity-0 group-hover:opacity-100 transition-opacity group-data-[status=active]:opacity-0"
                />
              </Link>
            ))}
          </nav>

          {/* RIGHT — utilities */}
          <div className="flex items-center gap-1.5 lg:gap-2 pr-6 lg:pr-8">
            <LangSwitch />
          </div>

        </div>
      </div>
    </header>
  );
}

